import { z } from 'zod';

import { type BaseItem } from './common.js';
import { type PollingSchedule } from './feed.js';
import { type FeedMessage, type SafeFeedMessage } from './feedMessage.js';
import { type Kind as ApiInstanceKind } from './orchestrator/apiInstance.js';

export enum ConnectionType {
  Sftp = 'Sftp',
  Api = 'Api',
  InterfaceEngine = 'InterfaceEngine',
  IntegrationEngine = 'IntegrationEngine',
}

export enum ApiInstanceMode {
  Test = 'Test',
  Production = 'Production',
}

export enum FeedStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum FivetranState {
  Active = 'Active',
  Syncing = 'Syncing',
  Error = 'Error',
  Inactive = 'Inactive',
}
export interface Connection {
  connectionType: ConnectionType;
  connectionId: string;
  name: string;
  source: string;
  destination: string;
  /**
   * FIXME - this field is in flux, and we are adjusting APIs during discovery phase
   * It will be standardized by 2025-12-31 or so.
   */
  method: string;
  /**
   * FIXME - this field is in flux, and we are adjusting APIs during discovery phase
   * It will be standardized by 2025-12-31 or so.
   */
  dataType: string;
  organizationId: string;
  lastUpdated: string;
  detailPath: string;
  lastActivityAt: string | null;
}

export type ConnectionSftp = Omit<Connection, 'connectionType' | 'lastUpdated'> & {
  connectionType: ConnectionType.Sftp;
  // created at timestamp
  created: string;
  // updated at timestamp
  updated: string;
  status: FeedStatus;
  filePath: string;
  pollingSchedule?: PollingSchedule;
  practiceIds: string[];
  flowIds: string[];
};

export type ConnectionApi = Omit<Connection, 'connectionType' | 'lastUpdated'> & {
  connectionType: ConnectionType.Api;
  // created at timestamp
  created: string;
  // updated at timestamp
  updated: string;
  deploymentName: string;
  clientId: string;
  kind: ApiInstanceKind;
  baseUrl: string;
  authenticationUrl: string;
  mrnURI: string | null;
  mode: ApiInstanceMode;
};

export type ConnectionInterfaceEngine = Omit<Connection, 'connectionType' | 'lastUpdated'> & {
  connectionType: ConnectionType.InterfaceEngine;
  port: string;
  // updated at timestamp
  updated: string;
  feedId: string | null;
  flowIds: string[];
};

export const ConnectionIntegrationEngineSchema = z.object({
  connectionType: z.literal(ConnectionType.IntegrationEngine),
  connectionId: z.string(),
  name: z.string(),
  source: z.string(),
  destination: z.string(),
  method: z.string(),
  dataType: z.string(),
  organizationId: z.string(),
  detailPath: z.string(),
  lastActivityAt: z.string().nullable(),
  updated: z.string(),
  fivetranConnectionId: z.string(),
  fivetranGroupId: z.string(),
  fivetranService: z.string(),
  state: z.nativeEnum(FivetranState),
});

export type ConnectionIntegrationEngine = z.infer<typeof ConnectionIntegrationEngineSchema>;

export interface FindAllOptions {
  source?: string;
  destination?: string;
  connectionType?: ConnectionType;
  dataType?: string;
  organizationId?: string;
  method?: string;
  q?: string;
}

export interface FindInterfaceEngineEventsOptions {
  detailPath: string;
  limit?: number;
  offset?: number;
}

export interface InterfaceEngineChannelStatistics {
  serverId: string;
  channelId: string;
  received: number;
  sent: number;
  error: number;
  filtered: number;
  queued: number;
}

export interface InterfaceEngineEventsResponse {
  messages: InterfaceEngineEvent[];
  channelStatistics: InterfaceEngineChannelStatistics;
}

export interface InterfaceEngineEvent {
  messageId: string;
  channel: string;
  serverId: string;
  receivedDateTime: string;
  processed: boolean;
  connectors: InterfaceEngineConnectorEvent[];
  feedMessage: FeedMessage | SafeFeedMessage;
}

export interface InterfaceEngineConnectorEvent {
  connectorName: string;
  metaDataId: string;
  status: InterfaceEngineConnectorEventMessageStatus;
  receivedDateTime: string;
  sendDateTime?: string;
  responseDateTime?: string;
  errorCode: number;
  sendAttempts: number;
  chainId: number;
  orderId: number;
  metadata: Record<string, unknown>;
}

export enum InterfaceEngineConnectorEventMessageStatus {
  Received = 'Received',
  Filtered = 'Filtered',
  Transformed = 'Transformed',
  Sent = 'Sent',
  Queued = 'Queued',
  Error = 'Error',
  Pending = 'Pending',
}

export enum InterfaceEngineAfterProcessingAction {
  None = 'None',
  Move = 'Move',
  Delete = 'Delete',
}

export enum ConnectionFileFilterMatchMode {
  Wildcard = 'Wildcard',
  Regex = 'Regex',
}

export interface CreateInterfaceEngineConnectorValues {
  name: string;
  organizationId: string;
  practiceId: string;
  ingestUrl: string;
  filePath: string;
  fileFilter: string;
  fileFilterMatchMode: ConnectionFileFilterMatchMode;
  afterProcessingAction: InterfaceEngineAfterProcessingAction;
  moveToDirectory?: string;
  moveToFileName?: string;
}

export interface InterfaceEngineChannel extends BaseItem {
  organizationId: string;
  name: string;
  description?: string;
}

/**
 * Resource synced from a connection (via resource-gateway)
 */
export interface ConnectionResource {
  id: string;
  resourceKey: string;
  name: string;
  createdAt: string | null;
  modifiedAt: string | null;
  data: Record<string, unknown>;
}

export interface GetConnectionResourcesOptions {
  connectionId: string;
  resourceKey: string;
  limit?: number;
  offset?: number;
}

/**
 * Response from signed URL endpoint for file preview/download
 */
export interface SignedUrlResponse {
  signedUrl: string;
  filename: string;
  expiresIn: number;
}

export interface GetFileSignedUrlOptions {
  fileId: string;
  organizationId: string;
  disposition?: 'inline' | 'attachment';
}
